import { getWebGenericResourceContentEntry } from '@csp/cps-web-content';
import { DEFAULT_ANONYMOUS_INSTRUCTIONS_VERSION } from '@csp/csp-common-config';
import {
  CountryNames,
  CountryNamesMapper,
  CountryNamesMock,
  LanguageNames,
  LanguageNamesMapper,
  memoizedToDebugContent,
} from '@csp/csp-common-content';
import {
  ContentDeliveryService,
  ContentstackConfig,
  ContentstackConfigService,
} from '@csp/csp-common-content-delivery-client';
import { ApiOptions, CountryCode, CspError } from '@csp/csp-common-model';
import { toRestOptions } from '@csp/csp-fe-auth';
import {
  AnonymousInstructionsContentModel,
  AnonymousInstructionsContentModelMock,
  ConsentDocumentContentModel,
  ConsentDocumentContentModelMock,
  ConsentDocumentCountryType,
  ConsentVideoContentModel,
  ConsentVideoContentModelMock,
  GenericPackageResourceContent,
  GenericWebContentPackage,
  LearnMoreContentModel,
  LearnMoreContentModelMock,
  LearnMoreIdentifierType,
  RandomisationStatusContentModel,
  RandomisationStatusContentModelMock,
  TechnicalSupportContentModel,
  TechnicalSupportContentModelMock,
  WebHcpAnonymousPatientInstructionsPdfExportBlock,
} from '@csp/csp-web-content-definitions';
import { ConsentType } from '@csp/dmdp-api-user-dto';
import { getOrDefault } from '../util/content/getOrDefault';
import { isDebugCmsModeActive } from '../util/debugCmsUtil';

let genericPackage: GenericWebContentPackage | undefined = undefined;

const learnMoreMock = LearnMoreContentModelMock.create();
const technicalSupportMock = TechnicalSupportContentModelMock.create();
const consentDocumentMock = ConsentDocumentContentModelMock.create();
const consentVideoMock = ConsentVideoContentModelMock.create();
const anonymousInstructionsMock = AnonymousInstructionsContentModelMock.create();
const countryNamesMock = CountryNamesMock.create();

const init = async (apiOptions?: ApiOptions): Promise<void> => {
  const isDebugEnabled = isDebugCmsModeActive();

  const unifyGenericPackage = await ContentDeliveryService.fetchContentPackage(
    'generic',
    toRestOptions(apiOptions),
    ContentstackConfigService.get(),
  );
  genericPackage = unifyGenericPackage as unknown as GenericWebContentPackage;
  if (isDebugEnabled) {
    genericPackage = memoizedToDebugContent<GenericWebContentPackage>(genericPackage, '', '[CS]');
  }
};

const get = (): GenericWebContentPackage => {
  if (!genericPackage) {
    throw CspError.badState('Must call GenericContentService.init() before trying to access generic content');
  }
  if (!genericPackage.resource) {
    throw CspError.badState('genericPackage.resource is not available in generic content package');
  }
  return genericPackage;
};
const getResource = (): GenericPackageResourceContent => get().resource;

const getLearnMore = (identifier: LearnMoreIdentifierType): LearnMoreContentModel => {
  const learnMore = get().learn_more?.find(learnMore => learnMore.identifier === identifier);

  return getOrDefault(
    learnMore,
    `Learn more content entry with identifier ${identifier} does not exist in the generic package`,
    learnMoreMock,
  );
};

const getTechnicalSupport = (): TechnicalSupportContentModel => {
  const technicalSupport = get().technical_support?.[0];

  return getOrDefault(
    technicalSupport,
    'Technical support content entry does not exist in the generic package',
    technicalSupportMock,
  );
};

const getConsentDocument = (
  consentType: ConsentType,
  version: string,
  countryCode: CountryCode,
): ConsentDocumentContentModel => {
  const consentDocument = get().consent_document?.find(
    consentDocument =>
      consentDocument.consent_type === consentType &&
      consentDocument.countries?.country?.includes(countryCode as ConsentDocumentCountryType) &&
      consentDocument.version === version,
  );

  return getOrDefault(
    consentDocument,
    `Consent document content entry with consent type ${consentType}, version ${version}, and country code ${countryCode} does not exist in the generic package`,
    consentDocumentMock,
  );
};

const fetchByLocale = async (locale: string, apiOptions?: ApiOptions): Promise<GenericWebContentPackage> => {
  const defaultConfig = ContentstackConfigService.get();
  if (defaultConfig.locale === locale) {
    return get();
  } else {
    const localeConfig: ContentstackConfig = {
      ...defaultConfig,
      locale,
      cacheProvider: {
        get: () => undefined,
        set: () => Promise.resolve(),
      },
    };
    const unifyGenericPackage = await ContentDeliveryService.fetchContentPackage(
      'generic',
      toRestOptions(apiOptions),
      localeConfig,
    );
    return unifyGenericPackage as unknown as GenericWebContentPackage;
  }
};

const fetchResourceByLocale = async (locale: string, apiOptions?: ApiOptions): Promise<GenericPackageResourceContent> =>
  (await fetchByLocale(locale, apiOptions)).resource;

const getLanguageNames = (): LanguageNames => LanguageNamesMapper.from(get());

const getCountryNames = (): CountryNames => {
  const countryNames = CountryNamesMapper.from(get());

  if (!countryNames?.countries?.length) {
    const errorMessage = 'No countries found in generic package. Using mock instead.';
    if (ContentstackConfigService.get().allowMockedContent) {
      console.debug(errorMessage);
      return countryNamesMock;
    } else {
      throw CspError.badState(errorMessage);
    }
  }

  return countryNames;
};

const getConsentVideo = (consentType: ConsentType, version: string): ConsentVideoContentModel => {
  const consentVideo = get().consent_video?.find(
    video => video.consent_type === consentType && video.version === version,
  );

  return getOrDefault(
    consentVideo,
    `Consent video content entry with consentType ${consentType} and version ${version} does not exist in the generic package`,
    consentVideoMock,
  );
};

const fetchAnonymousInstructionsByLocale = async (
  locale: string,
  version: string,
  apiOptions?: ApiOptions,
): Promise<AnonymousInstructionsContentModel> => {
  // TODO: [Bowser] remove this if-statement when the static entry has been migrated to V1 of the non-static content model
  if (version === DEFAULT_ANONYMOUS_INSTRUCTIONS_VERSION) {
    const content = await GenericContentService.fetchResourceByLocale(locale, apiOptions);
    const entry = getWebGenericResourceContentEntry(content, 'webHcpAnonymousPatientInstructions');

    if (!entry) {
      throw CspError.badState(`Entry webHcpAnonymousPatientInstructions does not exist for locale ${locale}`);
    }

    return mapStaticAnonymousIntructionsToNonStatic(entry.pdfExport);
  } else {
    const contentPackage = await fetchByLocale(locale, apiOptions);

    const anonymousInstructions = contentPackage.anonymous_instructions?.find(
      instruction => instruction.version.trim() === version.trim(),
    );

    return getOrDefault(
      anonymousInstructions,
      `Anonymous instructions content entry with version ${version} and locale ${locale} does not exist in the generic package`,
      anonymousInstructionsMock,
    );
  }
};

// TODO: [Bowser] remove this function when the static entry has been migrated to V1 of the non-static content model
const mapStaticAnonymousIntructionsToNonStatic = (
  staticContent: WebHcpAnonymousPatientInstructionsPdfExportBlock,
): AnonymousInstructionsContentModel => ({
  title: '',
  version: DEFAULT_ANONYMOUS_INSTRUCTIONS_VERSION,
  pdf: {
    welcome_message: {
      welcome_title: staticContent.header.welcomeTitle.text,
      welcome_message: staticContent.body.welcomeMessage.text,
    },
    instructions: {
      installation_instruction: staticContent.body.instructionsForPatient.markdown,
      login_instructions: staticContent.body.instructionsForPatientInstallation.markdown,
      app_name: staticContent.header.appName.text,
      title: staticContent.header.title.text,
    },
    login_details: {
      patient_login_details: staticContent.sectionHeader.patientLoginDetails.text,
      temporary_password: staticContent.textLabel.temporaryPassword.label,
      temporary_password_valid_until_date: staticContent.textLabel.temporaryPasswordValidDate.label,
      scan_qr_code_label: staticContent.body.scanCredentials.text,
      username: staticContent.textLabel.username.label,
    },
    support: {
      support_number: staticContent.textLabel.supportNumber.label,
      title: staticContent.sectionHeader.supportDetails.text,
      details: staticContent.body.supportDetails.text,
      patient_identifier: staticContent.textLabel.ecode.label,
    },
  },
  uid: '',
});

const getRandomisationStatus = (): RandomisationStatusContentModel => {
  const randomisationTexts = get().randomisation_status?.[0];
  return getOrDefault(
    randomisationTexts,
    'Randomisation status content entry does not exist in the generic package',
    RandomisationStatusContentModelMock.create(),
  );
};

export const GenericContentService = {
  init,
  get,
  getResource,
  getLearnMore,
  getLanguageNames,
  getCountryNames,
  fetchResourceByLocale,
  getTechnicalSupport,
  getConsentDocument,
  getConsentVideo,
  fetchAnonymousInstructionsByLocale,
  getRandomisationStatus,
};
